import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useToggle } from "react-use";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useParams, useHistory, Link } from "react-router-dom";

import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import DatePicker from "react-datepicker";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Table from "../../../../../components/Table";

import Input from "../../../../../components/Input";

import {
  MUTATION_CREATE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
  MUTATION_DELETE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
  MUTATION_DELETE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILE,
  MUTATION_REORDER_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILES,
  MUTATION_UPDATE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
} from "../../../../../config/graphql/mutation";

import { QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT } from "../../../../../config/graphql/query";

const AUDIENCE_MEMBERS: EntranceAgreementAudienceMember[] = [
  "employee",
  "visitor",
  "guest",
  "meetingAttendee",
];

type ValidityWindow = { validFrom?: Date; validUntil?: Date };

const getValidityState = (
  item: ValidityWindow,
  agreement?: ValidityWindow,
): "always" | "scheduled" | "active" | "expired" | "never" => {
  const starts = [item.validFrom, agreement?.validFrom].filter(
    Boolean,
  ) as Date[];

  const ends = [item.validUntil, agreement?.validUntil].filter(
    Boolean,
  ) as Date[];

  const validFrom = starts.length
    ? starts.reduce((latest, date) => (date > latest ? date : latest))
    : undefined;

  const validUntil = ends.length
    ? ends.reduce((earliest, date) => (date < earliest ? date : earliest))
    : undefined;

  if (!validFrom && !validUntil) {
    return "always";
  }

  if (
    validFrom &&
    validUntil &&
    dayjs(validFrom).startOf("day").isAfter(dayjs(validUntil).endOf("day"))
  ) {
    return "never";
  }

  if (validFrom && dayjs().isBefore(dayjs(validFrom).startOf("day"))) {
    return "scheduled";
  }

  if (validUntil && dayjs().isAfter(dayjs(validUntil).endOf("day"))) {
    return "expired";
  }

  return "active";
};

const SortableRow = ({
  item,
  position,
  agreement,
  screenId,
  agreementId,
}: {
  item: IEntranceAgreementFile;
  position: number;
  agreement?: ValidityWindow;
  screenId: string;
  agreementId: string;
}) => {
  const { id, file, approvalRequired, disabled, audience } = item;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "#f8f9fa" : undefined,
  };

  /**
   * Documents carry no title in practice — clients put everything inside the
   * file — so the row is identified by the position it is shown in and by the
   * uploaded file name.
   */
  const label = file?.filename ?? `#${position}`;

  const [visible, setVisible] = useToggle(false);

  const { t } = useTranslation(["digitalReceptionEntranceAgreement", "common"]);

  const enabledMembers = AUDIENCE_MEMBERS.filter(
    (member) => audience?.[member]?.enabled !== false,
  );

  const audienceLabel =
    enabledMembers.length === AUDIENCE_MEMBERS.length
      ? t(
          "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.audience.all",
        )
      : enabledMembers
          .map((member) =>
            t(
              `digitalReceptionEntranceAgreement:entranceAgreementFile.form.audience.${member}`,
            ),
          )
          .join(", ") ||
        t(
          "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.audience.none",
        );

  const validityState = getValidityState(item, agreement);

  const [onDelete] = useMutation(
    MUTATION_DELETE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILE,
    {
      refetchQueries: [
        {
          query: QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
          variables: { id: screenId },
        },
      ],
      variables: { id },
    },
  );

  const onBeforeDelete = () => {
    onDelete();

    return setVisible(false);
  };

  return (
    <>
      <tr ref={setNodeRef} style={style}>
        <td
          {...attributes}
          {...listeners}
          style={{
            cursor: "grab",
            width: 40,
            textAlign: "center",
            userSelect: "none",
            color: "#888",
            fontSize: 18,
          }}
          aria-label={t(
            "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.drag",
          )}
          title={t(
            "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.drag",
          )}
        >
          ☰
        </td>
        <th scope="row">{position}</th>
        <td>{file?.filename ?? "—"}</td>
        <td>{audienceLabel}</td>
        <td>
          {t(
            `digitalReceptionEntranceAgreement:entranceAgreementFiles.table.validity.${validityState}`,
          )}
        </td>
        <td>{approvalRequired.toString()}</td>
        <td>{disabled.toString()}</td>
        <td className="text-nowrap">
          <div className="d-flex">
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>

            <Link
              to={`/digital-receptions/${screenId}/settings/entrance-agreements/${agreementId}/files/${id}`}
            >
              <Button size="sm" variant="primary">
                {t("common:view")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-center">
            {t(
              "digitalReceptionEntranceAgreement:entranceAgreementFiles.modal.title",
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t(
            "digitalReceptionEntranceAgreement:entranceAgreementFiles.modal.body",
            {
              title: label,
            },
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

interface FieldValues {
  title: string;
  disabled: boolean;
  showFirst: boolean;
  validFrom?: Date;
  validUntil?: Date;
}

const EntranceAgreement = React.memo(() => {
  const [visible, setVisible] = useToggle(false);

  const history = useHistory();

  const { t } = useTranslation(["digitalReceptionEntranceAgreement", "common"]);

  const { screenId, agreementId } = useParams<{
    screenId: string;
    agreementId?: string;
  }>();

  const schema = useMemo(() => {
    return yup.object().shape({
      title: yup
        .string()
        .required(
          t("digitalReceptionEntranceAgreement:entranceAgreement.yup.title"),
        ),
      disabled: yup.boolean().required(),
      showFirst: yup.boolean().required(),
      validFrom: yup
        .date()
        .transform((v, o) => (o === null ? undefined : v))
        .notRequired(),
      validUntil: yup
        .mixed()
        .transform((v, o) => (o === null ? undefined : v))
        .test(
          "allow",
          t("digitalReceptionEntranceAgreement:entranceAgreement.yup.invalid"),
          function validate(value) {
            if (!(value instanceof Date)) {
              return true;
            }

            if (!this.parent.validFrom) {
              return true;
            }

            return dayjs(value).isAfter(dayjs(this.parent.validFrom));
          },
        ),
    });
  }, [t]);

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    defaultValues: {
      title: "",
      disabled: false,
      showFirst: true,
      validFrom: undefined,
      validUntil: undefined,
    },
  });

  const { data: entranceAgreementData, loading } = useQuery<{
    digitalReception: { entranceAgreement: IEntranceAgreement };
  }>(QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT, {
    variables: {
      id: screenId,
    },
    skip: !screenId,
  });

  const entranceAgreementFiles = useMemo(
    () =>
      [
        ...(entranceAgreementData?.digitalReception?.entranceAgreement?.files ??
          []),
      ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [entranceAgreementData],
  );

  const savedAgreement =
    entranceAgreementData?.digitalReception?.entranceAgreement;

  const [items, setItems] = useState<IEntranceAgreementFile[]>([]);

  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    if (reordering) {
      return;
    }

    setItems(entranceAgreementFiles);
  }, [entranceAgreementFiles, reordering]);

  const [onReorder] = useMutation(
    MUTATION_REORDER_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILES,
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const reordered = [...items];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    const previous = items;

    setItems(reordered);
    setReordering(true);

    try {
      await onReorder({
        variables: {
          items: reordered.map((item, index) => ({
            id: item.id,
            order: index,
          })),
        },
      });

      toast.success<string>(
        t(
          "digitalReceptionEntranceAgreement:entranceAgreementFiles.toast.reordered",
        ),
      );
    } catch (error: any) {
      setItems(previous);

      toast.error<string>(
        error?.message ??
          t(
            "digitalReceptionEntranceAgreement:entranceAgreementFiles.toast.reorderedError",
          ),
      );
    } finally {
      setReordering(false);
    }
  };

  useEffect(() => {
    if (entranceAgreementData?.digitalReception?.entranceAgreement) {
      const {
        digitalReception: {
          entranceAgreement: { validFrom, validUntil, ...rest },
        },
      } = entranceAgreementData;

      methods.reset({
        ...rest,
        validFrom: validFrom ? new Date(validFrom) : undefined,
        validUntil: validUntil ? new Date(validUntil) : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entranceAgreementData]);

  const [onUpdate] = useMutation(
    MUTATION_UPDATE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
    {
      refetchQueries: [
        {
          query: QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
          variables: { id: screenId },
        },
      ],
    },
  );
  const [onCreate] = useMutation(
    MUTATION_CREATE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
    {
      refetchQueries: [
        {
          query: QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
          variables: { id: screenId },
        },
      ],
    },
  );
  const [onDelete] = useMutation(
    MUTATION_DELETE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
    {
      refetchQueries: [
        {
          query: QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
          variables: { id: screenId },
        },
      ],
    },
  );

  const onBeforeDelete = useCallback(() => {
    return onDelete({
      variables: {
        id: agreementId,
      },
    })
      .then(() => {
        history.replace(`/digital-receptions/${screenId}/settings`);
        toast.success<string>(
          t(
            "digitalReceptionEntranceAgreement:entranceAgreement.toast.deleted",
          ),
        );
      })
      .catch(() => {
        toast.error<string>(
          t(
            "digitalReceptionEntranceAgreement:entranceAgreement.toast.deletedError",
          ),
        );
      });
  }, [history, onDelete, agreementId, screenId, t]);

  const onSubmit = useCallback(
    (values: FieldValues) => {
      const { title, disabled, showFirst, validFrom, validUntil } = values;

      const input = {
        title,
        disabled,
        showFirst,
        validFrom: validFrom ?? null,
        validUntil: validUntil ?? null,
      };

      if (!!agreementId) {
        return onUpdate({
          variables: {
            input: {
              id: agreementId,
              ...input,
            },
          },
        })
          .then(() => {
            toast.success<string>(
              t(
                "digitalReceptionEntranceAgreement:entranceAgreement.toast.updated",
              ),
            );
          })
          .catch(() => {
            toast.error<string>(
              t(
                "digitalReceptionEntranceAgreement:entranceAgreement.toast.updatedError",
              ),
            );
          });
      }

      return onCreate({
        variables: {
          input: {
            ...input,
            screen: screenId,
          },
        },
      })
        .then(
          ({
            data: {
              createEntranceAgreement: { id: entranceAgreementId },
            },
          }) => {
            toast.success<string>(
              t(
                "digitalReceptionEntranceAgreement:entranceAgreement.toast.created",
              ),
            );

            history.replace(
              `/digital-receptions/${screenId}/settings/entrance-agreements/${entranceAgreementId}`,
            );
          },
        )
        .catch(() => {
          toast.error<string>(
            t(
              "digitalReceptionEntranceAgreement:entranceAgreement.toast.createdError",
            ),
          );
        });
    },
    [history, screenId, onUpdate, onCreate, agreementId, t],
  );

  return (
    <div className="container-fluid">
      <FormProvider {...methods}>
        <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="form-group">
              <label htmlFor="title">
                {t(
                  "digitalReceptionEntranceAgreement:entranceAgreement.form.title",
                )}
              </label>
              <Input
                name="title"
                className="form-control"
                disabled={methods.watch("disabled")}
              />
            </div>
            <Controller
              name="showFirst"
              render={({ field: { value, name, onChange, ...rest } }) => {
                return (
                  <div className="form-check my-3">
                    <input
                      id={name}
                      {...rest}
                      type="checkbox"
                      className="form-check-input"
                      checked={value === true}
                      onChange={() => onChange(!value)}
                      disabled={methods.watch("disabled")}
                    />
                    <label className="form-check-label" htmlFor={name}>
                      {t(
                        "digitalReceptionEntranceAgreement:entranceAgreement.form.showFirst",
                      )}
                    </label>
                  </div>
                );
              }}
            />
            <div>
              <label htmlFor="validFrom">
                {t(
                  "digitalReceptionEntranceAgreement:entranceAgreement.form.validFrom",
                )}
              </label>
              <div className="mb-2">
                <Controller
                  name="validFrom"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <>
                      <div
                        className={clsx("", {
                          "is-invalid": !!error,
                        })}
                      >
                        <DatePicker
                          className={clsx("form-control", {
                            "is-invalid": !!error,
                          })}
                          // showTimeSelect
                          selected={value}
                          onChange={onChange}
                          // filterTime={(time) => dayjs() <= dayjs(time)}
                          // timeIntervals={30}
                          // dateFormat="dd/MM/yyyy p"
                          dateFormat="dd/MM/yyyy"
                          isClearable
                          shouldCloseOnSelect
                          disabled={methods.watch("disabled")}
                        />
                      </div>

                      <div className="invalid-feedback">
                        {error?.message ?? "Invalid"}
                      </div>
                    </>
                  )}
                />
              </div>
            </div>
            <div>
              <label htmlFor="validUntil">
                {t(
                  "digitalReceptionEntranceAgreement:entranceAgreement.form.validUntil",
                )}
              </label>
              <div>
                <Controller
                  name="validUntil"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <>
                      <div
                        className={clsx("mb3", {
                          "is-invalid": !!error,
                        })}
                      >
                        <DatePicker
                          className={clsx("form-control", {
                            "is-invalid": !!error,
                          })}
                          // showTimeSelect
                          selected={value}
                          onChange={onChange}
                          minDate={methods.watch("validFrom")}
                          // filterTime={(time) =>
                          //   dayjs(methods.getValues("validFrom")) < dayjs(time)
                          // }
                          // timeIntervals={30}
                          // dateFormat="dd/MM/yyyy p"
                          dateFormat="dd/MM/yyyy"
                          isClearable
                          shouldCloseOnSelect
                          disabled={methods.watch("disabled")}
                        />
                      </div>

                      <div className="invalid-feedback">
                        {error?.message ?? "Invalid"}
                      </div>
                    </>
                  )}
                />
              </div>
            </div>
            <Controller
              name="disabled"
              render={({ field: { value, name, onChange, ...rest } }) => {
                return (
                  <div className="form-check my-3">
                    <input
                      id={name}
                      {...rest}
                      type="checkbox"
                      className="form-check-input"
                      checked={value === true}
                      onChange={() => onChange(!value)}
                    />
                    <label className="form-check-label" htmlFor={name}>
                      {t(
                        "digitalReceptionEntranceAgreement:entranceAgreement.form.disabled",
                      )}
                    </label>
                  </div>
                );
              }}
            />
            <div className="mb-4">
              <button
                type="submit"
                className="btn btn-primary mt-4"
                disabled={loading}
              >
                {t(
                  `digitalReceptionEntranceAgreement:entranceAgreement.form.button.${
                    !agreementId ? "create" : "update"
                  }`,
                )}
              </button>
              {!!agreementId && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();

                    setVisible(true);
                  }}
                  className="btn btn-danger ml-3 mt-4"
                >
                  {t("common:delete")}
                </button>
              )}
            </div>
          </div>
          {!!agreementId && (
            <div className="col-lg-8 col-md-6 col-sm-12">
              <div className="d-flex justify-content-between align-items-center">
                <p>
                  {t(
                    "digitalReceptionEntranceAgreement:entranceAgreementFiles.title",
                    { count: 2 },
                  )}
                  {reordering && (
                    <small className="text-muted ml-2 font-italic">
                      {t(
                        "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.saving",
                      )}
                    </small>
                  )}
                </p>
                <Link
                  to={`/digital-receptions/${screenId}/settings/entrance-agreements/${agreementId}/files/new`}
                  type="button"
                  className="btn btn-primary"
                  style={{ marginTop: "-1.5rem" }}
                >
                  {t(
                    "digitalReceptionEntranceAgreement:entranceAgreementFiles.button.add",
                  )}
                </Link>
              </div>
              {items.length > 1 && (
                <div
                  className="d-flex align-items-center bg-light border rounded px-3 py-2 mb-3"
                  role="note"
                >
                  <span
                    aria-hidden="true"
                    style={{ fontSize: 18, color: "#6c757d", marginRight: 12 }}
                  >
                    ☰
                  </span>
                  <small className="text-muted mb-0">
                    {t(
                      "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.reorderHint",
                    )}
                  </small>
                </div>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th scope="col" style={{ width: 40 }}>
                        <span className="sr-only">
                          {t(
                            "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.drag",
                          )}
                        </span>
                      </th>
                      <th scope="col">
                        {t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.position",
                        )}
                      </th>
                      <th scope="col">
                        {t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.document",
                        )}
                      </th>
                      <th scope="col">
                        {t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.audience",
                        )}
                      </th>
                      <th scope="col">
                        {t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.validity",
                        )}
                      </th>
                      <th scope="col">
                        {t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.approvalRequired",
                        )}
                      </th>
                      <th scope="col">
                        {t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.disabled",
                        )}
                      </th>
                      <th scope="col">
                        {t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.th.actions",
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <SortableContext
                      items={items.map((item) => item.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {items.map((item, index) => (
                        <SortableRow
                          key={item.id}
                          item={item}
                          position={index + 1}
                          agreement={savedAgreement}
                          screenId={screenId}
                          agreementId={agreementId}
                        />
                      ))}
                    </SortableContext>
                    {!items.length && (
                      <tr>
                        <td colSpan={8} className="text-center">
                          {t(
                            "digitalReceptionEntranceAgreement:entranceAgreementFiles.table.noFiles",
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </DndContext>
            </div>
          )}
        </form>
      </FormProvider>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-center">
            {t(
              "digitalReceptionEntranceAgreement:entranceAgreement.modal.title",
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("digitalReceptionEntranceAgreement:entranceAgreement.modal.body")}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default EntranceAgreement;
